import React, { useRef, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth';
import { api, type ModeloDrone } from '@/lib/api';

const FORMATO_ICON: Record<string, string> = {
  b3dm: '🏗️', obj: '🧊', ply: '☁️', desconocido: '📦',
};
const FORMATO_LABEL: Record<string, string> = {
  b3dm: '3D Tiles (Cesium)', obj: 'Modelo OBJ', ply: 'Nube de puntos PLY', desconocido: 'Detectando…',
};

function formatBytes(bytes: string) {
  const n = Number(bytes);
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}

export default function DroneEquipoView() {
  const { token } = useAuthStore();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [viewerId, setViewerId] = useState<string | null>(null);

  const [form, setForm] = useState({ nombre: '', edificio: '', descripcion: '' });

  const { data: modelos = [], isLoading } = useQuery<ModeloDrone[]>({
    queryKey: ['modelos'],
    queryFn: () => api.modelos(token!),
    enabled: !!token,
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.eliminarModelo(token!, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['modelos'] }),
  });

  const handleFiles = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['zip', 'ply', 'obj', 'b3dm'].includes(ext ?? '')) {
      setError('Solo se permiten archivos ZIP, PLY, OBJ o B3DM');
      return;
    }
    setError('');
    // Auto-fill nombre from filename
    if (!form.nombre) {
      setForm(f => ({ ...f, nombre: file.name.replace(/\.[^.]+$/, '') }));
    }
    // Store file reference in the input
    if (fileRef.current) {
      const dt = new DataTransfer();
      dt.items.add(file);
      fileRef.current.files = dt.files;
    }
  }, [form.nombre]);

  async function handleUpload() {
    const file = fileRef.current?.files?.[0];
    if (!file) { setError('Selecciona un archivo primero'); return; }
    if (!form.nombre.trim()) { setError('El nombre del modelo es requerido'); return; }
    if (!form.edificio.trim()) { setError('El edificio/zona es requerido'); return; }
    setError('');
    setUploading(true);
    setProgress(0);

    const fd = new FormData();
    fd.append('archivo', file);
    fd.append('nombre', form.nombre.trim());
    fd.append('edificio', form.edificio.trim());
    if (form.descripcion) fd.append('descripcion', form.descripcion);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/modelos');
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
    };

    xhr.onload = () => {
      setUploading(false);
      if (xhr.status === 201) {
        qc.invalidateQueries({ queryKey: ['modelos'] });
        setForm({ nombre: '', edificio: '', descripcion: '' });
        if (fileRef.current) fileRef.current.value = '';
        setProgress(0);
      } else {
        try { setError(JSON.parse(xhr.responseText).error ?? 'Error al subir'); }
        catch { setError('Error al subir el archivo'); }
      }
    };
    xhr.onerror = () => { setUploading(false); setError('Error de conexión'); };
    xhr.send(fd);
  }

  return (
    <div style={{ position: 'absolute', inset: 0, background: '#0a1628', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>

      {/* Viewer iframe overlay */}
      {viewerId && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100, background: '#0a0a14', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '10px 16px', background: 'rgba(10,22,40,0.95)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
            <button onClick={() => setViewerId(null)}
              style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              ← Volver
            </button>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
              {modelos.find(m => m.id === viewerId)?.nombre}
            </span>
            <span style={{ fontSize: 10, background: 'rgba(96,165,250,0.15)', color: '#60a5fa', borderRadius: 6, padding: '3px 8px' }}>
              {FORMATO_LABEL[modelos.find(m => m.id === viewerId)?.formato ?? 'desconocido']}
            </span>
          </div>
          <iframe
            src={`/api/modelos/${viewerId}/viewer?token=${token}`}
            style={{ flex: 1, border: 'none', background: '#0a0a14' }}
            title="Visor 3D"
            allow="fullscreen"
          />
        </div>
      )}

      <div style={{ maxWidth: 800, width: '100%', margin: '0 auto', padding: '28px 20px 60px' }}>

        {/* Header */}
        <div style={{ marginBottom: 28, display: 'flex', alignItems: 'center', gap: 14 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#60a5fa' }}>Modelos 3D — Equipo Drone</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 3 }}>Sube exports de DJI Terra: ZIP (3D Tiles), PLY o OBJ</div>
          </div>
          <div style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>{modelos.length} modelo{modelos.length !== 1 ? 's' : ''}</div>
        </div>

        {/* Upload form */}
        <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 16, border: '1px solid rgba(255,255,255,0.08)', padding: '20px 22px', marginBottom: 28 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#60a5fa', marginBottom: 16 }}>SUBIR NUEVO MODELO</div>

          {/* Drop zone */}
          <div
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={e => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
            onClick={() => fileRef.current?.click()}
            style={{ border: `2px dashed ${dragging ? '#60a5fa' : 'rgba(255,255,255,0.12)'}`, borderRadius: 12, padding: '28px 16px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s', background: dragging ? 'rgba(96,165,250,0.06)' : 'transparent', marginBottom: 16 }}>
            <input ref={fileRef} type="file" accept=".zip,.ply,.obj,.b3dm" style={{ display: 'none' }} onChange={e => handleFiles(e.target.files)} />
            <div style={{ fontSize: 28, marginBottom: 8 }}>📂</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.6)' }}>
              {fileRef.current?.files?.[0]?.name ?? 'Arrastra el archivo o haz clic para seleccionar'}
            </div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>ZIP (3D Tiles DJI Terra) · PLY · OBJ · B3DM · Máx. 3 GB</div>
          </div>

          {/* Fields */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Nombre del modelo *</div>
              <input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                placeholder="Ej: Bloque A Torre Norte"
                style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Edificio / Zona *</div>
              <input value={form.edificio} onChange={e => setForm(f => ({ ...f, edificio: e.target.value }))}
                placeholder="Ej: Edificio Principal"
                style={inputStyle} />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={labelStyle}>Descripción</div>
            <input value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
              placeholder="Observaciones del vuelo, fecha, etc."
              style={inputStyle} />
          </div>

          {error && (
            <div style={{ fontSize: 11, color: '#ef4444', background: 'rgba(239,68,68,0.08)', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>{error}</div>
          )}

          {uploading && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Subiendo archivo…</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#60a5fa' }}>{progress}%</span>
              </div>
              <div style={{ height: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 3 }}>
                <div style={{ height: '100%', background: 'linear-gradient(90deg,#1d4ed8,#60a5fa)', width: `${progress}%`, borderRadius: 3, transition: 'width 0.3s' }} />
              </div>
              {progress === 100 && (
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>Procesando modelo en el servidor…</div>
              )}
            </div>
          )}

          <button onClick={handleUpload} disabled={uploading}
            style={{ width: '100%', padding: '11px', borderRadius: 11, background: uploading ? 'rgba(29,78,216,0.3)' : '#1d4ed8', border: 'none', color: '#fff', fontWeight: 700, fontSize: 13, cursor: uploading ? 'not-allowed' : 'pointer' }}>
            {uploading ? 'Subiendo…' : '⬆ Subir modelo'}
          </button>
        </div>

        {/* Models list */}
        <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', marginBottom: 12, letterSpacing: '0.08em' }}>MODELOS CARGADOS</div>

        {isLoading && (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', border: '2.5px solid rgba(96,165,250,0.2)', borderTopColor: '#60a5fa', animation: 'spin 700ms linear infinite', margin: '0 auto 10px' }} />
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Cargando modelos…</div>
          </div>
        )}
        {!isLoading && modelos.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>🛸</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)' }}>No hay modelos cargados aún</div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {modelos.map(m => (
            <div key={m.id} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 14, border: '1px solid rgba(255,255,255,0.07)', padding: '14px 16px', display: 'flex', gap: 14, alignItems: 'center' }}>
              <div style={{ fontSize: 30, flexShrink: 0 }}>{FORMATO_ICON[m.formato]}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#fff' }}>{m.nombre}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>{m.edificio}</div>
                {m.descripcion && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>{m.descripcion}</div>}
                <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                  <Tag>{FORMATO_LABEL[m.formato]}</Tag>
                  <Tag>{formatBytes(m.fileSize)}</Tag>
                  <Tag>{new Date(m.createdAt).toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' })}</Tag>
                  {m.subidoPor && <Tag>👤 {m.subidoPor.nombre}</Tag>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                {m.formato !== 'desconocido' && (
                  <button onClick={() => setViewerId(m.id)}
                    style={{ padding: '7px 14px', borderRadius: 9, background: '#1d4ed8', border: 'none', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span>▶</span> Ver 3D
                  </button>
                )}
                <button onClick={() => { if (confirm(`¿Eliminar "${m.nombre}"?`)) deleteMut.mutate(m.id); }}
                  style={{ padding: '7px 10px', borderRadius: 9, background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444', fontSize: 11, cursor: 'pointer' }}>
                  🗑
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 11px', borderRadius: 9,
  border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)',
  color: '#fff', fontSize: 12, outline: 'none', boxSizing: 'border-box',
};
const labelStyle: React.CSSProperties = {
  fontSize: 9.5, fontWeight: 700, color: 'rgba(255,255,255,0.35)',
  marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em',
};

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ fontSize: 9.5, background: 'rgba(255,255,255,0.07)', borderRadius: 5, padding: '2px 7px', color: 'rgba(255,255,255,0.45)' }}>
      {children}
    </span>
  );
}
